/**
 * Events menu: clickable list of events/custom events to go inside mp-drop-menu
 */
import debounce from 'lodash/debounce';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import keyBy from 'lodash/keyBy';

import {findKeyByValue} from 'common/util/typescript';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {ProjectFeatureFlag, SPECIAL_EVENTS} from 'common/report/constants';
import {customEventsStore} from 'common/report/stores/custom-events';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {topEventsStore} from 'common/report/stores/events';
import {currentProjectStore} from 'common/report/stores/project-details';
import {featuresStore} from 'common/report/stores/features';
import {renameEvent} from 'common/report/util/events';
import {getProfilePath} from 'common/report/util/profile/utils';
import {MpEvent} from 'common/util/definitions-types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getTooltipDataForEvent} from 'common/widgets/context-panel/util';
import {TRACKING_EVENTS} from 'common/widgets/query-block/constants';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {eventPropertyValuesClient} from 'common/report/queries/query-api/event-property-values-client';
import {MPProperties} from 'common/constants';

import {ChildMenu} from '../child-menu';
import template from '../child-menu-items-menu.jade';
import {getCustomEventItemsFiltered, getDataDefinitionsEventMenuItem, getEventMenuItem, orderByLabel} from '../util';
import {ANY_EVENT, SESSION_END_EVENT, SESSION_START_EVENT} from './constants';
import {EVENT_PROPERTIES, EventItem} from './types';

interface KeyedEventDataMap {
  [s: string]: EventItem;
}

interface StagedTag {
  type: string;
  name: string;
  count?: number;
}

interface EventsMenuState {
  eventData: EventItem[];
  // We store a keyed and identical set of eventData for faster lookups by id
  keyedEventData: KeyedEventDataMap;
  isMenuLoading: boolean;
  tooltipData: ContextPanelData;
  permissions: Set<string>;
  stagedTag: StagedTag;
  includeHidden: boolean;
}

export class EventsMenu extends ChildMenu<EventsMenuState> {
  private currentProjectStoreCallback: () => void;

  private customEventsStoreCallback: () => void;

  private eventsStoreCallback: () => void;

  private eventDefinitionsStoreCallback: () => void;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      // crazy hack: Please DO NOT use `sections`, this is a temp workaround for signal to display context panel
      // TODO: Zihe remove this attr
      sections: {type: `json`, default: []},
      'show-any-event': `boolean`,
      'show-session-event': `boolean`,
      'show-all-events': `boolean`,
      'show-top-events': `boolean`,
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case `data-group-id`:
      case `exclude-autotrack-events`: {
        this.fetchEventData();
        break;
      }
      case `open`: {
        this.update({includeHidden: false});
      }
    }
  }

  get config() {
    return {
      template,

      defaultState: {
        eventData: [],
        eventItems: [],
        keyedEventData: {},
        isMenuLoading: false,
        tooltipData: null,
        permissions: null,
        stagedTag: null,
        displayedItem: null,
        includeHidden: false,
      },

      helpers: {
        ...super.config.helpers,
        getMenuSections: () => this.getSections(),
        handleItemClick: (ev) =>
          this.handleItemClick(
            ev.detail.item,
            ev.detail.searchFilter,
            ev.detail.labelMatch,
            ev.detail.subLabelMatch,
            ev.detail.customMatch,
          ),
        showItemDetails: debounce((ev) => this.showItemDetails(ev.detail.item), 100),
        sectionsFromInput: (searchFilter, sections) => {
          const items = [];
          const numHiddenEventMatches = sections.find(({label}) => label === `Hidden Events`)?.items?.length || 0;
          if (!this.state.includeHidden && numHiddenEventMatches > 0) {
            items.push({
              label: `Show Hidden Matches (${numHiddenEventMatches})`,
              value: `showhidden`,
              icon: `caret-down`,
            });
          }

          items.push({
            label: searchFilter,
            value: searchFilter,
            icon: `plus`,
          });
          return [{items}];
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.customEventsStoreCallback = () => this.updateEventItems();
    customEventsStore.subscribe(this.customEventsStoreCallback);

    this.eventsStoreCallback = () => this.updateEventItems();
    topEventsStore.subscribe(this.eventsStoreCallback);

    this.eventDefinitionsStoreCallback = () => this.update();
    eventDefinitionsStore.subscribe(this.eventDefinitionsStoreCallback);

    this.currentProjectStoreCallback = () => this.update();
    currentProjectStore.subscribe(this.currentProjectStoreCallback).fetch({projectId: this.attr(`project-id`)});

    this.fetchEventData();
  }

  disconnectedCallback() {
    customEventsStore.unsubscribe(this.customEventsStoreCallback);
    this.customEventsStoreCallback = null;

    topEventsStore.unsubscribe(this.eventsStoreCallback);
    this.eventsStoreCallback = null;

    eventDefinitionsStore.unsubscribe(this.eventDefinitionsStoreCallback);
    this.eventDefinitionsStoreCallback = null;

    currentProjectStore.unsubscribe(this.currentProjectStoreCallback);
    this.currentProjectStoreCallback = null;

    super.disconnectedCallback();
  }

  getItemUniqueKey(item) {
    return item.value;
  }

  showCreationControls() {
    return !this.state.stagedTag;
  }

  handleCloseMenu() {
    this.helpers.showItemDetails.cancel();
    this.update({stagedTag: null});
  }

  handleItemClick(selected, searchFilter, labelMatch, subLabelMatch, customMatch) {
    if (selected.value && selected.value.type === `tag`) {
      this.trackEvent(TRACKING_EVENTS.LEXICON_TAG_SELECTED, {
        tag: selected.value.name,
        eventsCount: selected.value.count,
      });

      this.update({
        stagedTag: selected.value as StagedTag,
      });
      return;
    } else if (selected.value && selected.value === `showhidden`) {
      this.trackEvent(TRACKING_EVENTS.LEXICON_SHOW_HIDDEN);
      this.update({includeHidden: true});
      return;
    }

    const item: EventItem = pick(selected, EVENT_PROPERTIES);

    if (!findKeyByValue(SPECIAL_EVENTS, item.value)) {
      this.addRecentItem(ResourceType.Event, item);
    }

    this.trackSelection(this.trackingProps(selected, searchFilter, labelMatch, subLabelMatch, customMatch));
    this.dispatchEvent(new CustomEvent(`select`, {detail: {item}}));
    this.update({stagedTag: null});
  }

  private async getSampleEventsUrl(eventItem) {
    const projectId = this.attr(`project-id`);
    const workspaceId = this.attr(`workspace-id`);

    const eventName = eventItem?.value || eventItem;

    // Ignore tag and invalid items
    if (eventItem?.value?.type === `tag` || typeof eventName !== `string`) {
      return ``;
    }

    const results = await eventPropertyValuesClient.query({
      eventName,
      propertyName: MPProperties.DistinctId,
      projectId,
      workspaceId,
      limit: 10,
    });

    if (!results?.length) {
      return ``;
    }

    const distinctId = results[Math.floor(Math.random() * results.length)];
    const url = getProfilePath(distinctId, null, {
      includedEvents: [eventName],
    });

    return url;
  }

  private getTooltipData(
    item: EventItem,
    {isLoadingAction = false, sampleEventUrl = ``, canProvideSampleUrl = true} = {},
  ): ContextPanelData {
    const canWriteDataDefinition = currentProjectStore.value?.hasPermission(`write_data_definitions`);
    const eventDefinitions = Object.values(eventDefinitionsStore.value);

    if (!item) {
      return null;
    }

    const mpEvent: MpEvent = {
      name: item.value,
      ...(item.custom ? {custom: item.custom} : {}),
      ...(item.id ? {id: item.id} : {}),
      ...(item.isCollectEverything ? {is_collect_everything_event: item.isCollectEverything} : {}),
    };

    const canWriteCustomEvent = currentProjectStore.value?.hasPermission(`write_customevents`);

    return getTooltipDataForEvent(mpEvent, eventDefinitions, canWriteCustomEvent, {
      allowEdit: !this.attr(`hide-edit-controls`),
      allowDescriptionEdit: canWriteDataDefinition,
      isLoadingAction,
      sampleEventUrl,
      canProvideSampleUrl,
    });
  }

  async showItemDetails(item) {
    let eventItem = item;
    // TODO: Zihe will refactor signal so this transformation is not needed.
    if (item && item.value && item.value.kind === `event`) {
      eventItem = item.value;
    }

    this.update({tooltipData: this.getTooltipData(eventItem, {isLoadingAction: true})});

    const sampleEventUrl = await this.getSampleEventsUrl(eventItem);
    this.update({
      tooltipData: this.getTooltipData(eventItem, {
        sampleEventUrl,
        canProvideSampleUrl: !!sampleEventUrl,
        isLoadingAction: false,
      }),
    });
  }

  getSelectedHoverTooltipData(options): ContextPanelData {
    const selected = this.attr(`selected`);
    if (!selected) {
      return null;
    }
    return this.getTooltipData(
      selected,
      options || {
        isLoadingAction: true,
      },
    );
  }

  async prefetchTooltipOptions() {
    const selected = this.attr(`selected`);
    if (!selected) {
      return null;
    }

    const sampleEventUrl = await this.getSampleEventsUrl(selected);

    return {
      sampleEventUrl,
      canProvideSampleUrl: !!sampleEventUrl,
      isLoadingAction: false,
    };
  }

  getSelectedTrackingProps(): Obj {
    const selected = this.attr(`selected`);
    if (!selected) {
      return null;
    }

    const props = {'Entity type': `event`};
    if (selected.custom) {
      props[`Is custom`] = true;
    }
    return props;
  }

  getPersistenceKey() {
    return `events-menu`;
  }

  getEventTagsSection(): Section[] {
    const eventDefinitions = Object.values(eventDefinitionsStore.value);
    const tagCounts = eventDefinitions.reduce((counts, def) => {
      if (!def.hidden) {
        def.tags.forEach((tag) => (counts[tag] = counts[tag] ? counts[tag] + 1 : 1));
      }

      return counts;
    }, {});

    const eventTagItems = Object.entries(tagCounts).map(([tag, count]) => ({
      label: `${tag} (${count})`,
      icon: `tag`,
      hasCaret: true,
      value: {
        type: `tag`,
        count,
        name: tag,
      },
    }));

    return [
      {
        label: `Events tagged as`,
        items: eventTagItems.sort(orderByLabel),
        showLimit: 7,
      },
    ];
  }

  getHiddenEventSection(): Section[] {
    const eventDefinitions = Object.values(eventDefinitionsStore.value);
    const items = eventDefinitions.filter((def) => def.hidden).map(getDataDefinitionsEventMenuItem);
    return [
      {
        label: `Hidden Events`,
        isHidden: !this.state.includeHidden,
        items,
      },
    ];
  }

  getSections() {
    // TODO: Zihe remove this check, a quick and temp workaround for signal to display context panel
    if (this.attr(`sections`).length > 0) {
      return this.attr(`sections`);
    }
    const {isMenuLoading, selected, keyedEventData} = this.state;
    const selectedValue = selected ? selected.value : null;
    const eventData = this.state.eventData.map((ev) => ({...ev, isSelected: selectedValue === ev.value}));

    const ADDITIONAL_ITEMS_ATTR_MAPPING: {[p: string]: SectionItem[]} = {
      'show-any-event': [
        {
          ...ANY_EVENT,
          isSelected: !selectedValue,
        },
      ],
      'show-all-events': [
        {
          label: renameEvent(SPECIAL_EVENTS.ALL_EVENTS),
          value: SPECIAL_EVENTS.ALL_EVENTS,
          icon: `star-top-events`,
        },
      ],
    };

    // Only allow show-session-event if not B2B or rollup project
    if (!this.attr(`data-group-id`) && !this.attr(`show-special-dataset-property`)) {
      ADDITIONAL_ITEMS_ATTR_MAPPING[`show-session-event`] = [SESSION_START_EVENT, SESSION_END_EVENT];
    }

    // TODO(jakub) address top events issue
    // Only allow top events if not a rollup project
    if (!this.attr(`show-special-dataset-property`)) {
      ADDITIONAL_ITEMS_ATTR_MAPPING[`show-top-events`] = [
        {
          label: renameEvent(SPECIAL_EVENTS.TOP_EVENTS),
          value: SPECIAL_EVENTS.TOP_EVENTS,
          icon: `star-top-events`,
        },
      ];
    }

    // picks additional items from attributes
    const additionalItems = Object.entries(ADDITIONAL_ITEMS_ATTR_MAPPING).reduce((items, [key, values]) => {
      if (this.attr(key)) {
        values = values.map((value) => ({
          ...value,
          isSelected: Object.prototype.hasOwnProperty.call(value, `isSelected`)
            ? value.isSelected
            : value.value === selectedValue,
        }));

        return [...items, ...values];
      }

      return items;
    }, []);

    const section = {
      label: `Events`,
      isLoading: isMenuLoading,
      items: [...additionalItems, ...eventData],
    };

    const recentlyViewedSection = {
      label: `Recently viewed`,
      items: this.getRecentItems(ResourceType.Event).map((event) => ({
        ...event,
        ...(event.value ? keyedEventData[event.value] : {}),
      })),
    };

    const eventDefinitions = Object.values(eventDefinitionsStore.value);
    return this.state.stagedTag
      ? [
          {
            ...section,
            label: `Events tagged as ${this.state.stagedTag.name}`,
            items: eventDefinitions
              .filter((def) => def.tags && def.tags.includes(this.state.stagedTag.name))
              .map((def) => section.items.find(({name}) => name === def.name))
              .filter(Boolean),
          },
        ]
      : [recentlyViewedSection, ...this.getEventTagsSection(), section, ...this.getHiddenEventSection()];
  }

  selectedIcon() {
    return this.state.selected?.custom ? `custom` : `event`;
  }

  selectedLabel() {
    const {selected} = this.state;
    let selectedLabel = ``;

    if (selected) {
      const {renamed, label} = selected;

      // handle custom event loading
      if (selected.id && selected.custom) {
        selectedLabel = customEventsStore.value[selected.id]?.name || selected.label || `Loading...`;
      } else {
        selectedLabel = label;
      }

      return renamed ? `${renamed} (${selectedLabel})` : selectedLabel;
    }

    return selectedLabel;
  }

  // fetches top events and custom events simultaneously and appends
  // data as it comes, it is prefered to show something as soon as
  // it is available
  fetchEventData() {
    const {isMenuLoading} = this.state;
    const excludeCustomEvents = this.attr(`exclude-custom-events`);

    if (!this.projectId || isMenuLoading) {
      return;
    }

    try {
      this.update({
        isMenuLoading: true,
        eventData: [],
        keyedEventData: {},
      });

      topEventsStore.fetch(this.sourceAttrs);

      if (!excludeCustomEvents) {
        customEventsStore.fetch(this.sourceAttrs);
      }

      eventDefinitionsStore.fetch(this.sourceAttrs);
    } catch (err) {
      console.error(`Error loading events items`, err);
    }
  }

  getCustomEventItems(): EventItem[] {
    if (this.attr(`exclude-custom-events`)) {
      return [];
    }

    return getCustomEventItemsFiltered({
      customEventsStoreValues: customEventsStore.value,
      eventDefinitionsStoreValues: eventDefinitionsStore.value,
      excludeAutotrackEvents: this.attr(`exclude-autotrack-events`),
      dataGroupId: this.attr(`data-group-id`),
      eventData: this.state.eventData,
    });
  }

  getEventsItems(includeHidden = false) {
    let eventItems;
    if (featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.DataDefinitionsFetch)) {
      // we filter out custom events here to prevent double count. custom events from data definitions store & custom events store are stitched together in getCustomEventItems
      // eventually, we want to abstract the stitching to the definitions store
      eventItems = Object.values(eventDefinitionsStore.value)
        .filter((ed) => !ed.customEventId)
        .map(getDataDefinitionsEventMenuItem);
    } else {
      eventItems = Object.values(topEventsStore.value).map((event) => {
        return getEventMenuItem(event, eventDefinitionsStore.value[event.name]);
      });
    }

    if (!includeHidden) {
      return eventItems.filter((prop) => !prop.hidden);
    } else {
      return eventItems;
    }
  }

  updateEventItems() {
    const items = sortBy([...this.getEventsItems(), ...this.getCustomEventItems()], `label`);

    this.update({
      isMenuLoading: false,
      eventData: items,
      keyedEventData: keyBy(items, `value`),
    });
  }

  trackingProps(selected, searchFilter, labelMatch, subLabelMatch, customMatch) {
    return {
      eventName: `Select Event`,
      props: {
        'Event name': selected.label,
        'is custom event': !!selected.custom,
        'is autotrack event': !!selected.isCollectEverything,
        'is hidden entity': !!selected.hidden,
        'Search - filter': searchFilter,
        'Search - label match': labelMatch,
        'Search - sublabel match': subLabelMatch,
        'Search - custom match': customMatch,
        'Metadata Search Enabled': true,
      },
    };
  }
}

export const EventsMenuEl = defineAndMakeJsxFactory(`mp-events-menu`, EventsMenu);
